
import { useEffect, useMemo, useState } from 'react';
import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";
import { User, Search, Gamepad2, Sword, Zap, Brain, Ghost, Store, Gem } from "lucide-react";
import { useTranslations } from "next-intl";

import { Game, getEnabledGames } from "../data/games";
import { NotificationProvider } from "../components/notification-provider";
import { MockAuthProvider, useMockAuth } from "../contexts/MockAuthContext";
import MockAuthModal from "../components/MockAuthModal";
import StoreModal from "../components/store-modal";

const games = getEnabledGames();
const categories = [
  { id: 'Doge OS', iconImage: '/images/doge32.png' },
  { id: 'Adventure', icon: Sword },
  { id: 'Simulation', icon: Store },
  { id: 'Casual', icon: Gamepad2 },
  { id: 'Arcade', icon: Zap },
  { id: 'Moba', icon: Ghost },
  { id: 'Puzzle', icon: Brain },
] as const;

const categoryIds = new Set<string>(categories.map((category) => category.id));

const getSingleQueryValue = (value: string | string[] | undefined): string | null => {
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }

  return value ?? null;
};

function HomeContent() {
  const router = useRouter();
  const { isLoggedIn, openAuthModal, addRecentGame, gems } = useMockAuth();
  const tHome = useTranslations("home");
  const tApp = useTranslations("app");
  const tCats = useTranslations("categories");

  const [isStoreOpen, setIsStoreOpen] = useState(false);

  const handleGameClick = (game: Game) => {
    // If we want to strictly require login, we can keep this check. 
    // But since we default to logged in, this will mostly pass.
    if (!isLoggedIn) {
      openAuthModal();
      return;
    }

    // Track recent game
    addRecentGame(game.id);

    router.push(`/games/${game.id}`);
  };

  const handleProfileClick = () => {
    router.push('/profile');
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    if (!router.isReady) return;

    const categoryParam = getSingleQueryValue(router.query.category);
    setSelectedCategory(categoryParam && categoryIds.has(categoryParam) ? categoryParam : null);
  }, [router.isReady, router.query.category]);

  const handleCategoryClick = (categoryId: string) => {
    const nextCategory = selectedCategory === categoryId ? null : categoryId;
    setSelectedCategory(nextCategory);

    const nextQuery = { ...router.query };

    if (nextCategory) {
      nextQuery.category = nextCategory;
    } else {
      delete nextQuery.category;
    }

    router.replace(
      {
        pathname: router.pathname,
        query: nextQuery,
      },
      undefined,
      { shallow: true }
    );
  };

  // Filter games based on search and category
  const filteredGames = useMemo(() => {
    return games.filter(game => {
      const matchesSearch = game.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        game.category.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory ? game.category === selectedCategory : true;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  return (
    <>
      <Head>
        <title>{tApp("title")}</title>
        <meta name="description" content={tApp("description")} />
      </Head>

      <main className="home-scroll-container min-h-screen h-screen overflow-y-auto">
        <div className="mobile-container bg-black rounded-md shadow-xl my-1">
          <header className="flex items-center justify-between py-4 px-4">
            <div className="flex items-center">
              <div className="w-[40px] h-8 relative bg-transparent">
                <Image
                  src="/images/logo-white.png"
                  alt="Logo"
                  width={40}
                  height={32}
                  className="object-contain"
                  priority
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Gem Store Button */}
              <button
                onClick={() => setIsStoreOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] text-game-text hover:bg-[#2a3b50] transition-colors"
              >
                <span className="font-bold text-sm text-white">{gems}</span>
                <Gem className="w-4 h-4 text-purple-500 fill-purple-500" />
                {/* <Plus className="w-3 h-3 text-white" /> */}
              </button>

              {/* Profile Icon */}
              <button
                onClick={handleProfileClick}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-game-card hover:bg-[#2a3b50] transition-colors text-game-text"
                aria-label="Profile"
              >
                <User className="w-5 h-5" />
              </button>
            </div>
          </header>

          <div className="px-4 pb-4">
            {/* Fixed Top Banner */}
            <section className="mb-6">
              <div className="relative w-full rounded-2xl overflow-hidden border border-game-card">
                <Image
                  src="/images/home-banner.webp"
                  alt={tHome("bannerAlt")}
                  width={750}
                  height={300}
                  className="w-full h-auto object-cover select-none"
                  priority
                  sizes="100vw"
                />
              </div>
            </section>

            {/* Search Bar */}
            <div className="relative mb-6">
              <input
                type="text"
                placeholder={tHome("searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#1a1a1a] text-white pl-4 pr-12 py-3.5 rounded-2xl border border-[#2a2a2a] focus:border-cyan-500 focus:outline-none transition-colors placeholder-gray-500"
              />
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-xl bg-[#2a3b50] text-cyan-400"
              >
                <Search className="w-5 h-5" />
              </button>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-3 mb-8">
              {categories.map((cat) => {
                const isSelected = selectedCategory === cat.id;

                // Define colors for each category
                const getIconColor = (id: string) => {
                  switch (id) {
                    case 'Adventure': return 'text-[#FF7D54]'; // Orange/Red
                    case 'Simulation': return 'text-[#4ADE80]'; // Green
                    case 'Casual': return 'text-[#60A5FA]'; // Blue
                    case 'Arcade': return 'text-[#A78BFA]'; // Purple
                    case 'Moba': return 'text-[#FACC15]'; // Yellow
                    case 'Puzzle': return 'text-[#FB923C]'; // Orange
                    case 'Doge OS': return 'text-[#FBBF24]'; // Gold
                    default: return 'text-cyan-400';
                  }
                };

                const iconColor = getIconColor(cat.id);

                return (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryClick(cat.id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all ${isSelected
                      ? 'bg-[#1E232F] border-cyan-500/50'
                      : 'bg-[#1E232F] border-transparent hover:border-white/10'
                      }`}
                  >
                    {'iconImage' in cat ? (
                      <Image
                        src={cat.iconImage}
                        alt=""
                        width={16}
                        height={16}
                        className="w-4 h-4 object-contain"
                      />
                    ) : (
                      <cat.icon className={`w-4 h-4 ${iconColor}`} />
                    )}
                    <span className={`font-medium text-sm ${isSelected ? 'text-white' : 'text-gray-400'}`}>
                      {tCats.has(cat.id) ? tCats(cat.id) : cat.id}
                    </span>
                  </button>
                );
              })}
            </div>

            <section>
              <h2 className="text-game-text text-lg font-semibold mb-4">
                {searchQuery || selectedCategory ? tHome("games") : tHome("games")}
              </h2>
              <div className="grid grid-cols-2 gap-2">
                {filteredGames.map((game, index) => (
                  <GameCard
                    key={game.id}
                    game={game}
                    index={index}
                    onClick={() => handleGameClick(game)}
                  />
                ))}
                {filteredGames.length === 0 && (
                  <div className="col-span-2 text-center py-10 text-gray-500">
                    No games found
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>
      {/* Store Modal */}
      <StoreModal
        isOpen={isStoreOpen}
        onClose={() => setIsStoreOpen(false)}
      />
      {/* Mock Auth Modal - still here if needed but unlikely to trigger */}
      <MockAuthModal />
    </>
  );
}

interface GameCardProps {
  game: Game;
  index: number;
  onClick: () => void;
}

function GameCard({ game, index, onClick }: GameCardProps) {
  const tCats = useTranslations("categories");
  // Helper to safely get translation or fallback to original category
  // This handles cases where translation might be missing for new categories
  const categoryName = tCats.has(game.category) ? tCats(game.category) : game.category;

  return (
    <div onClick={onClick} className="game-card">
      <div className="game-image">
        <Image
          src={game.image}
          alt={game.title}
          fill
          className="object-cover select-none"
          sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
          draggable={false}
          loading={index < 4 ? 'eager' : 'lazy'}
        />
      </div>

      <div className="game-info">
        <div className="title-row">
          <h3 className="game-title">{game.title}</h3>
          {game.isNew && <div className="badge new">NEW</div>}
          {game.isHot && <div className="badge hot">HOT</div>}
        </div>
        <div className="divider"></div>
        <p className="game-category">{categoryName}</p>
      </div>
    </div>
  );
}

export default function HomePage() {
  return (
    <NotificationProvider>
      <MockAuthProvider>
        <HomeContent />
      </MockAuthProvider>
    </NotificationProvider>
  );
}
